import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";

const formSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  country: z.object({
    name: z.string().optional(),
    // Optional, mirroring the API: billing `country_code` is
    // @ApiOptionalCountryCode() (IsString + IsOptional, no IsNotEmpty), so an
    // empty value is accepted. 54 of the last 96 000 returns have no billing
    // country at all — requiring one would dead-lock Save on them for a
    // merchant who only wants to fix a name or a zip.
    code: z.string().optional(),
  }),
  province_code: z.string().optional(),
  zip: z.string().optional(),
  city: z.string().optional(),
});

export default function useEditBillingAddressForm(
  returnRequest: ReturnRequestDto,
) {
  const { billingAddress } = returnRequest;

  return useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: billingAddress.first_name ?? "",
      lastName: billingAddress.last_name ?? "",
      address1: billingAddress.address1 ?? "",
      address2: billingAddress.address2 ?? "",
      zip: billingAddress.zip ?? "",
      city: billingAddress.city ?? "",
      // See the shipping-address form. It matters more here: `code` is optional
      // on this route, so a save that never touched the country would otherwise
      // go through and blank the stored country name.
      country: {
        name: billingAddress.country ?? "",
        code: billingAddress.country_code ?? "",
      },
      province_code: billingAddress.province_code ?? "",
      phone: billingAddress.phone ?? "",
    },
  });
}
